/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConfigSection, ConfigSwitchSection } from '../../../common';
import { Button } from '../../../components/core';
import { useMerchant } from '../../../contexts/MerchantContext';
import {
  useCreateCredentialMutation,
  useGetCredentialsQuery,
} from '../../../store/modules/merchant/api';
import type { SupportCredentialConfiguration } from '../../../store/modules/merchant/types';
import { toast } from '../../../util/toast';
import { useEllaDraft } from '../EllaDraftContext';
import { FORM_MAX_WIDTH } from '../ellaFormStyles';
import { HelpCenterCrawlSection } from './HelpCenterCrawlSection';
import { isGorgiasDomain, normalizeGorgiasDomain } from '../supportDomain';

const buildSchema = (isApiKeyRequired: boolean) =>
  z.object({
    domain: z
      .string()
      .min(1, 'Gorgias domain is required')
      .transform(normalizeGorgiasDomain)
      .refine(isGorgiasDomain, 'Enter your Gorgias account host, e.g. store.gorgias.com'),
    email: z.string().trim().email('Enter the agent email for the Gorgias API key'),
    apiKey: isApiKeyRequired ? z.string().trim().min(1, 'API key is required') : z.string().trim(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export const SupportForm = (): JSX.Element => {
  const { merchantId } = useMerchant();
  const { draft, readOnly, updateDraft } = useEllaDraft();
  const support = draft.support!;
  const knowledgeBase = draft.knowledgeBase ?? { enabled: false };

  const { data: credentialsData, isLoading: isCredentialLoading } = useGetCredentialsQuery(
    { merchantId, type: ['support'] },
    { skip: !merchantId },
  );
  const [createCredential, { isLoading: isSavingCredential }] = useCreateCredentialMutation();

  const existingCredential = credentialsData?.support?.credentials?.[0];
  const existingConfiguration = (
    existingCredential?.data as
      { configuration?: Partial<SupportCredentialConfiguration> } | undefined
  )?.configuration;
  const hasStoredApiKey = Boolean(existingConfiguration?.apiKey);

  const schema = useMemo(() => buildSchema(!hasStoredApiKey), [hasStoredApiKey]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { domain: '', email: '', apiKey: '' },
  });

  // reset() fills registered fields behind MUI's back, so the labels are shrunk up front.
  useEffect(() => {
    reset({
      domain: existingConfiguration?.domain ?? '',
      email: existingConfiguration?.email ?? '',
      apiKey: '',
    });
  }, [existingConfiguration, reset]);

  const submit = async ({ domain, email, apiKey }: FormValues) => {
    const configuration = {
      domain,
      email,
      // A blank key on an existing connection keeps the stored one (it is never shown back).
      apiKey: apiKey || existingConfiguration?.apiKey || '',
    };

    try {
      await createCredential({
        merchantId,
        credentialType: 'support',
        systemOfOrigin: 'gorgias',
        data: { configuration },
      }).unwrap();
      toast.success('Helpdesk connection saved');
    } catch {
      toast.error('Failed to save the helpdesk connection');
    }
  };

  return (
    <Box
      component="section"
      id="ella-support-form"
      data-testid="ella-support-form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <ConfigSwitchSection
        title="Support module"
        description="The shopping agent answers questions covered by your help center and files helpdesk tickets for the rest. Needs a connected helpdesk to go live."
        switchLabelTitle="Support"
        isChecked={support.enabled}
        onToggle={(next) => {
          updateDraft((prev) => ({
            ...prev,
            support: { ...prev.support!, enabled: next },
          }));
        }}
        disabled={readOnly}
        switchTestId="ella-support-enabled-switch"
      />

      <ConfigSwitchSection
        title="Help center answers"
        description={
          support.enabled
            ? 'Included with the support module.'
            : 'The shopping agent answers store questions from your help center. When it has no answer, they give the shopper your escalation contact. No helpdesk needed.'
        }
        switchLabelTitle="Help center answers"
        isChecked={support.enabled || knowledgeBase.enabled}
        onToggle={(next) => {
          updateDraft((prev) => ({
            ...prev,
            knowledgeBase: { enabled: next },
          }));
        }}
        disabled={readOnly || support.enabled}
        switchTestId="ella-knowledge-base-enabled-switch"
      />

      <HelpCenterCrawlSection />

      <ConfigSection
        title="Helpdesk connection"
        description="Gorgias account the shopping agent files tickets into. The API key is stored encrypted and never shown again."
        marginBottom={2}
      >
        <Stack
          component="form"
          noValidate
          spacing={2.5}
          sx={{ maxWidth: FORM_MAX_WIDTH }}
          onSubmit={handleSubmit(submit)}
          data-testid="ella-support-credential-form"
        >
          {!isCredentialLoading && (
            <Typography variant="small" color="text.secondary" sx={{ m: 0 }}>
              {existingCredential
                ? `Connected to ${existingConfiguration?.domain ?? 'a Gorgias account'}.`
                : 'No helpdesk connected yet.'}
            </Typography>
          )}
          <TextField
            {...register('domain')}
            label="Gorgias domain"
            placeholder="store.gorgias.com"
            required
            disabled={readOnly}
            error={!!errors.domain}
            helperText={errors.domain?.message}
            inputProps={{ 'aria-label': 'Gorgias domain' }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register('email')}
            label="Agent email"
            placeholder="agent@store.com"
            required
            disabled={readOnly}
            error={!!errors.email}
            helperText={errors.email?.message ?? 'The Gorgias user the API key belongs to.'}
            inputProps={{ 'aria-label': 'Agent email' }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register('apiKey')}
            label="API key"
            type="password"
            required={!hasStoredApiKey}
            disabled={readOnly}
            error={!!errors.apiKey}
            helperText={
              errors.apiKey?.message ??
              (hasStoredApiKey ? 'Leave blank to keep the stored key.' : undefined)
            }
            inputProps={{ 'aria-label': 'Gorgias API key' }}
            InputLabelProps={{ shrink: true }}
          />
          <Stack direction="row" justifyContent="flex-start">
            <Button
              text="Save connection"
              type="submit"
              loading={isSavingCredential}
              disabled={readOnly}
            />
          </Stack>
        </Stack>
      </ConfigSection>
    </Box>
  );
};
